import CoreUtils from '../utils/CoreUtils';
import NumberUtils from '../utils/NumberUtils';
import SataConfigurationUtils from '../value-types/SataConfigurationUtils';
import QuickEntryConfigurationUtils from '../value-types/QuickEntryConfigurationUtils';
import StringUtils, { spaceCharCode, hyphenCharCode } from '../utils/StringUtils';
import DateTimeUtils from '../utils/DateTimeUtils';
import TimeUtils from '../utils/TimeUtils';
import CutOffUtils from '../value-types/CutOffUtils';

export default class Validation {
  static validateValue(value, metadataRule) {

    switch (metadataRule.type) {
      case 'boolean':
        return Validation.validateBooleanValue(value, metadataRule);
      case 'cutoff':
        return Validation.validateCutOffValue(value, metadataRule);
      case 'datetime':
        return Validation.validateDateTimeValue(value, metadataRule);
      case 'guid':
        return Validation.validateGuidValue(value, metadataRule);
      case 'number':
        return Validation.validateNumberValue(value, metadataRule);
      case 'sata-config':
        return Validation.validateSataConfigurationValue(value, metadataRule);
      case 'quick-entry-config':
        return Validation.validateQuickEntryConfigurationValue(value, metadataRule);
      case 'select':
        return Validation.validateSelectValue(value, metadataRule);
      case 'string':
        return Validation.validateStringValue(value, metadataRule);
      case 'string-list':
        return Validation.validateStringListValue(value, metadataRule);
      case 'timespan':
        return Validation.validateTimeSpanValue(value, metadataRule);
      default:
        return null;
    }
  }

  static validateBooleanValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && CoreUtils.isUndefinedOrNull(value))
      return 'Value cannot be empty.';

    if (CoreUtils.isUndefinedOrNull(value))
      return null;

    if (typeof value !== 'boolean')
      return 'Value must be a boolean type.';

    return null;
  }

  static validateCutOffValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && CoreUtils.isUndefinedOrNull(value))
      return 'Value cannot be empty.';

    if (CoreUtils.isUndefinedOrNull(value))
      return null;

    var cutOffType = CutOffUtils.parseType(value);
    var cutOffTime = CutOffUtils.parseTime(value);

    if (CoreUtils.isUndefinedOrNull(cutOffType) || CoreUtils.isUndefinedOrNull(cutOffTime))
      return 'Value is not in a valid CutOff value format.';

    var timeValues = TimeUtils.parseTime(cutOffTime);

    if (cutOffType === CutOffUtils.weccTradingDayPrefix) {
      if (timeValues.hours < 0 || timeValues.minutes < 0 || timeValues.seconds < 0 || timeValues.milliseconds < 0)
        return 'Time value must have a valid time.'

      if (timeValues.hours > 23 || timeValues.minutes > 59 || timeValues.seconds > 59 || timeValues.milliseconds > 999)
        return 'Time value must have a valid time.'
    } else {
      if (timeValues.hours < 0 || timeValues.minutes < 0 || timeValues.seconds < 0 || timeValues.milliseconds < 0)
        return 'Time value must have a *positive* time offset.'
    }

    return null;
  }

  static validateDateTimeValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && CoreUtils.isUndefinedOrNull(value))
      return 'Value cannot be empty.';

    if (CoreUtils.isUndefinedOrNull(value))
      return null;

    if (!DateTimeUtils.isValidDate(value))
      return 'Value is not a valid date.';

    if (metadataRule.restrictionType === 'DateTime')
      return null;

    if (!DateTimeUtils.isDateOnly(value))
      return 'Value must only be a date (ie. no time component).';

    if (metadataRule.restrictionType === 'DateOnlyStartOfMonth' && !DateTimeUtils.isFirstDayOfMonth(value))
      return 'Value must be the *first* day of a month.';

    if (metadataRule.restrictionType === 'DateOnlyEndOfMonth' && !DateTimeUtils.isLastDayOfMonth(value))
      return 'Value must be the *first* day of a month.';

    return null;
  }

  static validateGuidValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && CoreUtils.isUndefinedOrNull(value))
      return 'Value cannot be empty.';

    if (CoreUtils.isUndefinedOrNull(value))
      return null;

    return null;
  }

  static validateNumberValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && CoreUtils.isUndefinedOrNull(value))
      return 'Value cannot be empty.';

    if (CoreUtils.isUndefinedOrNull(value))
      return null;

    if (typeof value !== 'number')
      return 'Value must be a number type.';

    if (!metadataRule.allowFractional && value % 1 !== 0)
      return 'Value must be an integer (no fractional amounts).';

    if (metadataRule.minimumValue > value)
      return `Value cannot be less than ${NumberUtils.formatNumber(metadataRule.minimumValue)}.`;

    if (metadataRule.maximumValue < value)
      return `Value cannot be greater than ${NumberUtils.formatNumber(metadataRule.maximumValue)}.`;

    return null;
  }

  static validateSataConfigurationValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && StringUtils.isNullOrEmpty(value))
      return 'Value cannot be empty.';

    if (StringUtils.isNullOrEmpty(value))
      return null;

    var sataConfigurationValues = SataConfigurationUtils.deserializeValues(value);
    if (CoreUtils.isUndefinedOrNull(sataConfigurationValues))
      return 'Value is not a Sata Configuration.';

    return null;
  }

  static validateQuickEntryConfigurationValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && StringUtils.isNullOrEmpty(value))
      return 'Value cannot be empty.';

    if (StringUtils.isNullOrEmpty(value))
      return null;

    var quickEntryConfigurationValues = QuickEntryConfigurationUtils.deserializeValues(value);
    if (CoreUtils.isUndefinedOrNull(quickEntryConfigurationValues))
      return 'Value is not a Quick Entry Configuration.';

    return null;
  }

  static validateSelectValue(value, metadataRule) {
    if (metadataRule.options.indexOf(value) === -1)
      return 'Value is not a valid option.';

    return null;
  }

  static validateStringValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && StringUtils.isNullOrEmpty(value))
      return 'Value cannot be empty.';

    if (StringUtils.isNullOrEmpty(value))
      return null;

    if (typeof value !== 'string')
      return 'Value must be a string type.';

    if (metadataRule.isAlphaNumeric && !StringUtils.isAlphaNumericString(value, metadataRule.allowSpaces, metadataRule.allowHyphens))
      return metadataRule.allowSpaces
        ? 'Value can only contain letters, digits or spaces.'
        : 'Value can only contain letters or digits (no spaces).';

    if (!metadataRule.allowSpaces && StringUtils.indexOfCharCode(value, spaceCharCode) !== -1)
      return 'Value cannot contain spaces.';

    if (!metadataRule.allowHyphens && StringUtils.indexOfCharCode(value, hyphenCharCode) !== -1)
      return 'Value cannot contain hyphens.';

    if (metadataRule.maximumLength < value.length)
      return `Value cannot exceed ${metadataRule.maximumLength} characters in length.`;

    return null;
  }

  static validateStringListValue(value, metadataRule) {
    if (!StringUtils.isStringArray(value))
      return 'Value is not a valid string list array.';

    if (!metadataRule.allowEmpty && value.length < 1)
      return 'Value cannot be an empty array.';

    return null;
  }

  static validateTimeSpanValue(value, metadataRule) {
    if (!metadataRule.allowEmpty && CoreUtils.isUndefinedOrNull(value))
      return 'Value cannot be empty.';

    if (CoreUtils.isUndefinedOrNull(value))
      return null;

    if (TimeUtils.isValidTime(value))
      return 'Value must be a time type.';

    if (TimeUtils.compareTimes(metadataRule.minimumValue, value) === 1)
      return `Value cannot be less than ${TimeUtils.formatShortTime(metadataRule.minimumValue)}.`;

    if (TimeUtils.compareTimes(metadataRule.maximumValue, value) === -1)
      return `Value cannot be greater than ${TimeUtils.formatShortTime(metadataRule.maximumValue)}.`;

    return null;
  }
}